import { z } from "zod";
import {
  READINESS_DIMENSIONS,
  actorSchema,
  dimensionResultSchema,
  idSchema,
  instantSchema,
  readinessSchema,
} from "@amber/contracts";
import { inFixedOrder, languageTagSchema, scoreSchema } from "./shared.js";

export const NO_TRACKED_PERSON = "No tracked person identified";

export const ASSESSMENT_TRIGGERS = [
  "publication",
  "correction",
  "speaker_confirmation",
  "reported_problem_opened",
  "reported_problem_resolved",
  "passage_check_failed",
  "passage_check_invalidated",
  "access_change",
  "exclusion",
  "policy_version",
  "recheck",
  "integrity_failed",
  "integrity_restored",
] as const;
export const assessmentTriggerSchema = z.enum(ASSESSMENT_TRIGGERS);

const dimensionOutcomeSchema = z
  .strictObject({ result: dimensionResultSchema, reason: z.string().min(1).nullable() })
  .refine((d) => d.result === "passed" || d.reason !== null, "a dimension that did not pass has a reason");

const assessmentDimensionsSchema = inFixedOrder(
  READINESS_DIMENSIONS,
  z.strictObject({
    original: dimensionOutcomeSchema,
    provenance: dimensionOutcomeSchema,
    language: dimensionOutcomeSchema,
    context: dimensionOutcomeSchema,
    text: dimensionOutcomeSchema,
    attribution: dimensionOutcomeSchema,
    problems: dimensionOutcomeSchema,
    access: dimensionOutcomeSchema,
  }),
);

export const readinessAssessmentRecordSchema = z
  .strictObject({
    id: idSchema("ass"),
    communication_id: idSchema("com"),
    evidence_revision_id: idSchema("rev"),
    profile_id: idSchema("prf").nullable(),
    policy_version_id: idSchema("pol"),
    dimensions: assessmentDimensionsSchema,
    result: readinessSchema,
    reason: z.string().min(1).nullable(),
    assessed_at: instantSchema,
    trigger: assessmentTriggerSchema,
  })
  .refine(
    (a) =>
      (a.result === "eligible") ===
      (a.profile_id !== null &&
        READINESS_DIMENSIONS.every((d) => ["passed", "inapplicable"].includes(a.dimensions[d].result))),
    "eligible exactly when a tracked person is assessed and every dimension passed or is inapplicable",
  )
  .refine(
    (a) => (a.result === "eligible") === (a.reason === null),
    "a draft result has a reason and an eligible one has none",
  )
  .refine((a) => {
    if (a.profile_id === null) return a.reason === NO_TRACKED_PERSON;
    const first = READINESS_DIMENSIONS.map((d) => a.dimensions[d]).find(
      (o) => o.result === "blocked" || o.result === "not_run",
    );
    return first === undefined || a.reason === first.reason;
  }, "a draft reason is the reason of the first blocked or not-run dimension, or that no tracked person was identified");
export type ReadinessAssessmentRecord = z.infer<typeof readinessAssessmentRecordSchema>;

export const POLICY_VERSION_STATES = ["draft", "applied", "superseded"] as const;
export const policyVersionStateSchema = z.enum(POLICY_VERSION_STATES);

export const POLICY_REQUIREMENTS = ["required", "not_required"] as const;
const policyRequirementSchema = z.enum(POLICY_REQUIREMENTS);

export const policyVersionRecordSchema = z
  .strictObject({
    id: idSchema("pol"),
    name: z.string().min(1),
    version: z.number().int().positive(),
    state: policyVersionStateSchema,
    dimensions: inFixedOrder(
      READINESS_DIMENSIONS,
      z.strictObject({
        original: policyRequirementSchema,
        provenance: policyRequirementSchema,
        language: z.strictObject({ required: z.boolean(), languages: z.array(languageTagSchema).min(1) }),
        context: policyRequirementSchema,
        text: policyRequirementSchema,
        attribution: z.strictObject({ required: z.boolean(), min_unit_attribution_confidence: scoreSchema }),
        problems: policyRequirementSchema,
        access: policyRequirementSchema,
      }),
    ),
    applied_at: instantSchema.nullable(),
    applied_by: actorSchema.nullable(),
    reason: z.string().min(1),
    impact_preview: z
      .strictObject({ would_change: z.number().int().nonnegative(), computed_at: instantSchema })
      .optional(),
    revision: z.number().int().positive(),
  })
  .refine(
    (p) => (p.state === "draft") === (p.applied_at === null && p.applied_by === null),
    "only a draft lacks applied_at and applied_by",
  )
  .refine(
    (p) => p.state !== "draft" || p.impact_preview !== undefined,
    "a draft policy version carries an impact preview",
  );
export type PolicyVersionRecord = z.infer<typeof policyVersionRecordSchema>;
